import { FirstStockLocationNotice } from '../../app/no-stock-location-notice.js';
import { AdaptiveTable } from '../../app/adaptive-table.js';
import { BarcodeReaderField } from '../../app/barcode-reader-field.js';
import {
  Button,
  CurrencyInput,
  DatePicker,
  Input,
  Modal,
  Select,
  StatusPill,
  Textarea,
  formatCivilDate,
  formatCurrency,
  todayCivilDate,
  useSnackbar,
  type CivilDate,
  IconButton,
} from '@hue/design-system';
import { PickField } from './pick-field.js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, ShoppingCart, Trash2, Ban } from 'lucide-react';
import { useRef, useState } from 'react';
import {
  EmptyCard,
  ErrorCard,
  FilterBar,
  LoadingCard,
  Pagination,
  RequestAlert,
} from '../../app/data-view.js';
import type { PurchasingView } from '../../app/list-views.js';
import type { ViewSetter } from '../../app/view-state.js';
import {
  getApiError,
  inventoryApi,
  purchasingApi,
  type GoodsReceipt,
  type PurchaseItemInput,
  type PurchaseOrder,
  type PurchaseOrderStatus,
  type SupplierOffering,
} from '../../lib/api.js';
import type { PurchasingCapabilities } from './shared.js';

const statusLabels: Record<PurchaseOrderStatus, string> = {
  OPEN: 'Aberto',
  COMPLETED: 'Recebido',
  CANCELED: 'Cancelado',
};
type DraftLine = PurchaseItemInput & { key: string };

export function PurchasingMovementsPanel({
  kind,
  capabilities,
  timeZone,
  showUnits,
  view,
  onView,
}: {
  kind: 'orders' | 'receipts';
  capabilities: PurchasingCapabilities;
  timeZone: string;
  showUnits: boolean;
  view: PurchasingView;
  onView: ViewSetter<PurchasingView>;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [open, setOpen] = useState(false);
  const [requestError, setRequestError] = useState<string>();
  const filters = {
    ...(view.search.trim() ? { search: view.search.trim() } : {}),
    ...(view.supplier ? { supplierId: view.supplier } : {}),
    limit: view.limit,
    offset: view.offset,
    ...(kind === 'orders' && view.orderStatus ? { status: view.orderStatus } : {}),
  };
  const query = useQuery({
    queryKey: ['company', 'purchasing', kind, filters],
    queryFn: async (): Promise<{ rows: Array<PurchaseOrder | GoodsReceipt>; total: number }> => {
      if (kind === 'orders') {
        const page = await purchasingApi.listOrders(filters);
        return { rows: page.orders, total: page.total };
      }
      const page = await purchasingApi.listReceipts(filters);
      return { rows: page.receipts, total: page.total };
    },
  });
  const rows = query.data?.rows ?? [];
  const total = query.data?.total ?? 0;
  const canCreate =
    kind === 'orders'
      ? capabilities.canManageOrders === true
      : capabilities.canManageReceipts === true;

  return (
    <div className="space-y-4">
      {canCreate ? (
        <div className="flex justify-end">
          <Button onClick={() => setOpen(true)}>
            <Plus className="size-4" />
            {kind === 'orders' ? 'Novo pedido' : 'Registrar recebimento'}
          </Button>
        </div>
      ) : null}
      <FilterBar
        searchId={`purchasing-${kind}-search`}
        searchLabel={kind === 'orders' ? 'Buscar pedido' : 'Buscar recebimento'}
        searchPlaceholder="Número, fornecedor ou documento"
        search={view.search}
        onSearchChange={(search) => onView({ search, offset: 0 })}
        chips={[]}
        onClear={() => onView({ search: '', orderStatus: undefined, offset: 0 })}
        count={`${String(total)} registro${total === 1 ? '' : 's'}`}
        resultsLabel={`Ver ${String(total)} registro${total === 1 ? '' : 's'}`}
        pageSize={view.limit}
        onPageSizeChange={(limit) => onView({ limit, offset: 0 })}
      >
        {kind === 'orders' ? (
          <Select
            label="Situação"
            value={view.orderStatus ?? ''}
            options={[
              { value: '', label: 'Todas' },
              ...Object.entries(statusLabels).map(([value, label]) => ({ value, label })),
            ]}
            onValueChange={(value) =>
              onView({
                orderStatus: (value || undefined) as PurchaseOrderStatus | undefined,
                offset: 0,
              })
            }
          />
        ) : null}
      </FilterBar>
      {requestError ? <RequestAlert message={requestError} /> : null}
      {query.isLoading ? (
        <LoadingCard label="Carregando compras" />
      ) : query.isError ? (
        <ErrorCard message={getApiError(query.error)} />
      ) : rows.length === 0 ? (
        <EmptyCard
          icon={ShoppingCart}
          title={kind === 'orders' ? 'Nenhum pedido' : 'Nenhum recebimento'}
          description={
            kind === 'orders'
              ? 'Cadastre a expectativa de uma compra com prazo.'
              : 'Registre o que efetivamente chegou ou foi prestado.'
          }
        />
      ) : (
        <MovementTable
          kind={kind}
          rows={rows}
          showUnits={showUnits}
          onCancel={async (order) => {
            setRequestError(undefined);
            try {
              await purchasingApi.cancelOrder(order.id);
              await queryClient.invalidateQueries({ queryKey: ['company', 'purchasing'] });
              snackbar.success('Pedido cancelado.');
            } catch (cause) {
              setRequestError(getApiError(cause));
            }
          }}
        />
      )}
      {total > 0 ? (
        <Pagination
          offset={view.offset}
          limit={view.limit}
          total={total}
          onOffsetChange={(offset) => onView({ offset })}
          itemLabel={(count) => (count === 1 ? 'registro' : 'registros')}
        />
      ) : null}
      <MovementModal
        kind={kind}
        open={open}
        onOpenChange={setOpen}
        timeZone={timeZone}
        onSaved={async () => {
          await queryClient.invalidateQueries({ queryKey: ['company', 'purchasing'] });
          setOpen(false);
          snackbar.success(kind === 'orders' ? 'Pedido cadastrado.' : 'Recebimento registrado.');
        }}
      />
    </div>
  );
}

function MovementTable({
  kind,
  rows,
  showUnits,
  onCancel,
}: {
  kind: 'orders' | 'receipts';
  rows: Array<PurchaseOrder | GoodsReceipt>;
  showUnits: boolean;
  onCancel: (order: PurchaseOrder) => Promise<void>;
}) {
  return (
    <div className="overflow-hidden rounded-2xl border border-surface-border bg-surface-card">
      <AdaptiveTable>
        <table className="w-full text-left text-sm">
          <thead className="bg-surface-subtle text-content-muted">
            <tr>
              <th className="px-5 py-3">Número</th>
              <th className="px-5 py-3">Fornecedor</th>
              {showUnits ? <th className="px-5 py-3">Unidade</th> : null}
              <th className="px-5 py-3">Data</th>
              <th className="px-5 py-3">Itens</th>
              <th className="px-5 py-3">Total</th>
              <th className="px-5 py-3">Situação</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const order = kind === 'orders' ? (row as PurchaseOrder) : null;
              const receipt = kind === 'receipts' ? (row as GoodsReceipt) : null;
              const total =
                receipt?.totalCents ??
                order!.items.reduce(
                  (sum, item) => sum + Math.round(Number(item.quantity) * item.unitPriceCents),
                  0,
                );
              const currency = row.items[0]!.currency;
              return (
                <tr key={row.id} className="border-t border-surface-border">
                  <td className="px-5 py-4 font-semibold">#{row.number}</td>
                  <td className="px-5 py-4">{row.supplierDisplayName}</td>
                  {showUnits ? <td className="px-5 py-4">{row.businessUnitName}</td> : null}
                  <td className="px-5 py-4">
                    {formatCivilDate(receipt?.receivedOn ?? order!.orderedOn)}
                  </td>
                  <td className="px-5 py-4">{row.items.length}</td>
                  <td className="px-5 py-4">{formatCurrency(total, currency)}</td>
                  <td className="px-5 py-4">
                    {order ? (
                      <div className="flex items-center gap-2">
                        <StatusPill
                          tone={
                            order.status === 'OPEN'
                              ? 'warning'
                              : order.status === 'COMPLETED'
                                ? 'positive'
                                : 'neutral'
                          }
                        >
                          {statusLabels[order.status]}
                        </StatusPill>
                        {order.status === 'OPEN' ? (
                          <IconButton
                            label="Cancelar"
                            icon={<Ban />}
                            tone="danger"
                            onClick={() => void onCancel(order)}
                          />
                        ) : null}
                      </div>
                    ) : (
                      <StatusPill tone="positive">Recebido</StatusPill>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </AdaptiveTable>
    </div>
  );
}

function MovementModal({
  kind,
  open,
  onOpenChange,
  timeZone,
  onSaved,
}: {
  kind: 'orders' | 'receipts';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  timeZone: string;
  onSaved: () => Promise<void>;
}) {
  const today = todayCivilDate(timeZone);
  const [supplierId, setSupplierId] = useState('');
  const [orderId, setOrderId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [primaryDate, setPrimaryDate] = useState<CivilDate | null>(today);
  const [dueDate, setDueDate] = useState<CivilDate | null>(today);
  const [secondaryDate, setSecondaryDate] = useState<CivilDate | null>(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([
    { key: crypto.randomUUID(), offeringId: '', quantity: '1', unitPriceCents: 0 },
  ]);
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [scanQuantity, setScanQuantity] = useState('1');
  const [scannedOfferings, setScannedOfferings] = useState<SupplierOffering[]>([]);
  const scannedKeys = useRef(new Set<string>());
  const scanScope = useRef(0);
  const suppliers = useQuery({
    queryKey: ['company', 'purchasing', 'movement-suppliers'],
    queryFn: () =>
      purchasingApi.listSuppliers({
        activity: 'ACTIVE',
        kind: 'ALL',
        sort: 'displayName',
        direction: 'asc',
        limit: 100,
        offset: 0,
      }),
    enabled: open,
  });
  const offerings = useQuery({
    queryKey: ['company', 'purchasing', 'movement-offerings', supplierId],
    queryFn: () =>
      purchasingApi.listOfferings({
        activity: 'ACTIVE',
        kind: 'ALL',
        supplierId,
        sort: 'displayName',
        direction: 'asc',
        limit: 100,
        offset: 0,
      }),
    enabled: open && Boolean(supplierId),
  });
  const orders = useQuery({
    queryKey: ['company', 'purchasing', 'open-orders'],
    queryFn: () => purchasingApi.listOrders({ status: 'OPEN', limit: 100, offset: 0 }),
    enabled: open && kind === 'receipts',
  });
  const catalog = useQuery({
    queryKey: ['company', 'purchasing', 'purchasable-products'],
    queryFn: () => purchasingApi.listPurchasableProducts(),
    enabled: open && kind === 'receipts',
  });
  const locations = useQuery({
    queryKey: ['company', 'inventory', 'locations'],
    queryFn: () => inventoryApi.listLocations('ACTIVE'),
    enabled: open && kind === 'receipts' && catalog.data?.available === true,
  });
  const locationCount = locations.data?.length ?? 0;
  const onlyLocationId = locationCount === 1 ? locations.data![0]!.id : undefined;
  if (onlyLocationId && locationId !== onlyLocationId) setLocationId(onlyLocationId);
  const available = [
    ...scannedOfferings,
    ...(offerings.data?.offerings ?? []).filter(
      (item) => !scannedOfferings.some((found) => found.id === item.id),
    ),
  ];
  const hasProduct = lines.some(
    (line) => available.find((item) => item.id === line.offeringId)?.kind === 'PRODUCT',
  );
  const offeringOptions = [
    { value: '', label: 'Escolha a oferta' },
    ...available.map((item) => ({ value: item.id, label: item.displayName })),
  ];
  const supplierOptions = [
    { value: '', label: 'Escolha o fornecedor' },
    ...(suppliers.data?.suppliers ?? []).map((item) => ({
      value: item.id,
      label: item.displayName,
    })),
  ];
  const orderOptions = [
    { value: '', label: 'Sem pedido' },
    ...(orders.data?.orders ?? []).map((item) => ({
      value: item.id,
      label: `#${String(item.number)} · ${item.supplierDisplayName}`,
    })),
  ];

  function chooseOrder(value: string) {
    setOrderId(value);
    scannedKeys.current.clear();
    scanScope.current += 1;
    setScannedOfferings([]);
    const order = orders.data?.orders.find((item) => item.id === value);
    if (!order) return;
    setSupplierId(order.supplierId);
    setLines(
      order.items
        .filter((item) => Number(item.receivedQuantity) < Number(item.quantity))
        .map((item) => ({
          key: crypto.randomUUID(),
          offeringId: item.offeringId,
          purchaseOrderItemId: item.id,
          quantity: String(Number(item.quantity) - Number(item.receivedQuantity)),
          unitPriceCents: item.unitPriceCents,
        })),
    );
  }
  async function readBarcode(code: string) {
    setError(undefined);
    if (!supplierId) {
      setError('Escolha o fornecedor antes da leitura.');
      return;
    }
    const amount = scanQuantity.replace(',', '.');
    if (!/^\d+(?:\.\d{1,6})?$/u.test(amount) || Number(amount) <= 0) {
      setError('Informe uma quantidade positiva com até seis casas para a leitura.');
      return;
    }
    const scope = scanScope.current;
    try {
      const catalog = await purchasingApi.listPurchasableProducts(code);
      const hits = catalog.products
        .filter((product) => product.active)
        .flatMap((product) =>
          product.packages
            .filter((pack) => pack.active && pack.barcode === code)
            .map((pack) => ({ product, pack })),
        );
      const hit = hits.length === 1 ? hits[0] : undefined;
      if (!hit) {
        setError('Código não encontrado. Confira o EAN da apresentação em Catálogo / Produtos.');
        return;
      }
      const page = await purchasingApi.listOfferings({
        activity: 'ACTIVE',
        kind: 'PRODUCT',
        supplierId,
        productId: hit.product.id,
        sort: 'displayName',
        direction: 'asc',
        limit: 100,
        offset: 0,
      });
      const candidates = page.offerings.filter(
        (item) => item.active && item.packageId === hit.pack.id,
      );
      const ordered = orderId
        ? (orders.data?.orders
            .find((item) => item.id === orderId)
            ?.items.filter(
              (item) => item.productId === hit.product.id && item.packageId === hit.pack.id,
            ) ?? [])
        : [];
      const offering =
        orderId && ordered.length === 1
          ? candidates.find((item) => item.id === ordered[0]!.offeringId)
          : candidates.length === 1
            ? candidates[0]
            : undefined;
      if (!offering || (orderId && ordered.length !== 1)) {
        setError(
          orderId
            ? 'A apresentação não identifica um único item deste pedido. Selecione e informe a quantidade no item correto.'
            : 'Cadastre ou escolha manualmente a oferta deste fornecedor para a apresentação lida.',
        );
        return;
      }
      if (scope !== scanScope.current) return;
      setScannedOfferings((items) => [
        ...items.filter((item) => item.id !== offering.id),
        offering,
      ]);
      const orderItem = ordered[0];
      const key = orderItem?.id ?? offering.id;
      const alreadyScanned = scannedKeys.current.has(key);
      setLines((items) => {
        const existing = items.find((item) =>
          orderId ? item.purchaseOrderItemId === orderItem?.id : item.offeringId === offering.id,
        );
        if (existing)
          return items.map((item) =>
            item.key === existing.key
              ? {
                  ...item,
                  quantity:
                    orderId && !alreadyScanned ? amount : addQuantities(item.quantity, amount),
                }
              : item,
          );
        const line = {
          key: crypto.randomUUID(),
          offeringId: offering.id,
          quantity: amount,
          unitPriceCents: orderItem?.unitPriceCents ?? offering.unitPriceCents,
          ...(orderItem ? { purchaseOrderItemId: orderItem.id } : {}),
        };
        return [...items.filter((item) => item.offeringId), line];
      });
      scannedKeys.current.add(key);
    } catch (cause) {
      setError(getApiError(cause));
    }
  }

  async function save() {
    setError(undefined);
    if (
      !supplierId ||
      !primaryDate ||
      lines.some(
        (line) => !line.offeringId || Number(line.quantity) <= 0 || line.unitPriceCents <= 0,
      )
    ) {
      setError('Preencha fornecedor, data e todos os itens.');
      return;
    }
    if (kind === 'receipts' && (!dueDate || (hasProduct && !locationId && locationCount > 0))) {
      setError(hasProduct ? 'Escolha o local de entrada dos produtos.' : 'Informe o vencimento.');
      return;
    }
    setSaving(true);
    try {
      if (kind === 'orders')
        await purchasingApi.createOrder({
          supplierId,
          orderedOn: primaryDate,
          expectedOn: secondaryDate,
          notes: notes || null,
          items: lines.map(stripLine),
        });
      else
        await purchasingApi.createReceipt({
          idempotencyKey: crypto.randomUUID(),
          supplierId,
          purchaseOrderId: orderId || null,
          locationId: hasProduct && locationId ? locationId : null,
          receivedOn: primaryDate,
          dueOn: dueDate!,
          invoiceNumber: invoiceNumber || null,
          notes: notes || null,
          items: lines.map(stripLine),
        });
      await onSaved();
    } catch (cause) {
      setError(getApiError(cause));
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={kind === 'orders' ? 'Cadastrar pedido de compra' : 'Registrar recebimento'}
      description={
        kind === 'orders'
          ? 'Registre o que foi solicitado ao fornecedor.'
          : 'Registre o que realmente chegou, mesmo sem pedido prévio.'
      }
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button loading={saving} onClick={() => void save()}>
            {kind === 'orders' ? 'Cadastrar pedido' : 'Registrar recebimento'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error ? <RequestAlert message={error} /> : null}
        {kind === 'receipts' ? (
          <PickField
            label="Pedido de compra (opcional)"
            placeholder="Sem pedido — buscar pelo número ou fornecedor"
            value={orderId}
            options={orderOptions}
            onChange={chooseOrder}
          />
        ) : null}
        <PickField
          label="Fornecedor"
          placeholder="Buscar fornecedor"
          value={supplierId}
          options={supplierOptions}
          disabled={Boolean(orderId)}
          onChange={(value) => {
            setSupplierId(value);
            scannedKeys.current.clear();
            scanScope.current += 1;
            setScannedOfferings([]);
            setLines([
              { key: crypto.randomUUID(), offeringId: '', quantity: '1', unitPriceCents: 0 },
            ]);
          }}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <DatePicker
            label={kind === 'orders' ? 'Data do pedido' : 'Data do recebimento'}
            value={primaryDate}
            onValueChange={setPrimaryDate}
            today={today}
          />
          {kind === 'orders' ? (
            <DatePicker
              label="Previsão de entrega (opcional)"
              value={secondaryDate}
              onValueChange={setSecondaryDate}
              today={today}
            />
          ) : (
            <DatePicker
              label="Vencimento"
              value={dueDate}
              onValueChange={setDueDate}
              today={today}
            />
          )}
        </div>
        {kind === 'receipts' ? (
          <Input
            label="Nota ou documento (opcional)"
            value={invoiceNumber}
            onChange={(event) => setInvoiceNumber(event.target.value)}
          />
        ) : null}
        {kind === 'receipts' && hasProduct && locations.isSuccess && locationCount === 0 ? (
          <FirstStockLocationNotice />
        ) : kind === 'receipts' && hasProduct ? (
          <Select
            label="Local de entrada"
            value={locationId}
            options={[
              { value: '', label: 'Escolha o local' },
              ...(locations.data ?? []).map((item) => ({
                value: item.id,
                label: item.displayName,
              })),
            ]}
            onValueChange={setLocationId}
          />
        ) : null}
        {kind === 'receipts' && catalog.data?.available ? (
          <div className="space-y-3 rounded-xl border border-surface-border p-4">
            <Input
              label="Quantidade por leitura"
              value={scanQuantity}
              inputMode="decimal"
              className="max-w-32"
              onChange={(event) => setScanQuantity(event.target.value)}
            />
            <BarcodeReaderField
              onRead={readBarcode}
              disabled={saving}
              scopeKey={`${supplierId}:${orderId}`}
            />
            <p className="text-sm text-content-muted">
              A quantidade é de apresentações, como caixas ou frascos. No pedido, a primeira leitura
              substitui a quantidade prevista do item; as seguintes somam. Confira preço, local e
              quantidades antes de registrar.
            </p>
          </div>
        ) : null}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Itens</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setLines((items) => [
                  ...items,
                  { key: crypto.randomUUID(), offeringId: '', quantity: '1', unitPriceCents: 0 },
                ])
              }
            >
              <Plus className="size-4" />
              Adicionar
            </Button>
          </div>
          {lines.map((line) => (
            <div
              key={line.key}
              className="grid gap-3 rounded-xl border border-surface-border p-4 sm:grid-cols-[1fr_8rem_10rem_auto]"
            >
              <PickField
                label="Oferta"
                placeholder="Buscar oferta"
                value={line.offeringId}
                options={offeringOptions}
                disabled={Boolean(orderId)}
                onChange={(value) =>
                  setLines((items) =>
                    items.map((item) =>
                      item.key === line.key
                        ? {
                            ...item,
                            offeringId: value,
                            unitPriceCents:
                              available.find((offering) => offering.id === value)?.unitPriceCents ??
                              0,
                          }
                        : item,
                    ),
                  )
                }
              />
              <Input
                label="Quantidade"
                value={line.quantity}
                onChange={(event) =>
                  setLines((items) =>
                    items.map((item) =>
                      item.key === line.key ? { ...item, quantity: event.target.value } : item,
                    ),
                  )
                }
              />
              <CurrencyInput
                label="Preço unitário"
                value={line.unitPriceCents || null}
                onValueChange={(cents) =>
                  setLines((items) =>
                    items.map((item) =>
                      item.key === line.key ? { ...item, unitPriceCents: cents ?? 0 } : item,
                    ),
                  )
                }
              />
              <IconButton
                label="Remover item"
                icon={<Trash2 />}
                tone="danger"
                disabled={lines.length === 1}
                onClick={() => setLines((items) => items.filter((item) => item.key !== line.key))}
              />
            </div>
          ))}
        </div>
        <Textarea
          label="Observação (opcional)"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </div>
    </Modal>
  );
}

function stripLine(line: DraftLine): PurchaseItemInput {
  return {
    offeringId: line.offeringId,
    quantity: line.quantity,
    unitPriceCents: line.unitPriceCents,
    ...(line.purchaseOrderItemId ? { purchaseOrderItemId: line.purchaseOrderItemId } : {}),
  };
}

function addQuantities(left: string, right: string): string {
  const scaled = (value: string) => {
    const [whole, fraction = ''] = value.replace(',', '.').split('.');
    return BigInt(`${whole}${fraction.padEnd(6, '0')}`);
  };
  const sum = scaled(left) + scaled(right);
  return `${sum / 1_000_000n}.${String(sum % 1_000_000n).padStart(6, '0')}`;
}
